import {
  CUSTOM_PERSONAL_MESSAGE_MAX_CHARACTERS,
  TEXTFIELD_MAX_CHARACTERS,
} from './constraints'

import {
  ERROR_AMOUNT_REQUIRED,
  ERROR_CUSTOM_AMOUNT_INVALID,
  ERROR_CUSTOM_PERSONAL_MESSAGE,
  ERROR_CUSTOM_MESSAGE_REQUIRED,
  ERROR_DELIVERY_DATE_INVALID,
  ERROR_DELIVERY_METHOD_REQUIRED,
  ERROR_EMAIL_INVALID,
  ERROR_EMAIL_MISSING,
  ERROR_FROM_NAME,
  ERROR_FROM_NAME_REQUIRED,
  ERROR_MOBILE_NUMBER_INVALID,
  ERROR_MOBILE_NUMBER_MISSING,
  ERROR_RECIPIENT_NAME,
  ERROR_RECIPIENT_NAME_REQUIRED,
  createErrorDeliveryDateValidRangeExceeded,
  TERMS_AND_CONDITIONS_REQUIRED,
  createErrorMaximumStringLengthExceeded,
} from './error-messages'

import moment from 'moment'
import { LABEL_CUSTOM_MESSAGE } from '../types/product'
import { DeliveryMethods } from '../types/cart'
import { MarketplaceProductOptionType } from '../types/marketplace-product'
import { Currency } from '../../shared/lib/currency'
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator'

export type ValidationResult = string | undefined

export const isEmpty = (value?: string): boolean => {
  return !(value && value.length)
}

export const validateStrMaxLen = (
  value?: string,
  maxLen: number = TEXTFIELD_MAX_CHARACTERS
): boolean => Boolean(value && value.length <= maxLen)

/**
 * Follows the same implementation on the server
 * https://github.com/ScentreGroup/commerce-service/blob/master/src/lib/validation.ts#L5
 */
export const validateEmail = (value?: string): ValidationResult => {
  if (!value || !value.length) {
    return ERROR_EMAIL_MISSING
  }

  const reg =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  if (reg.test(value)) {
    return undefined
  }

  return ERROR_EMAIL_INVALID
}

/**
 * Checks if given string is a correct australian mobile number.
 *
 * Uses the pattern described [here](https://en.wikipedia.org/wiki/Telephone_numbers_in_Australia#Mobile_phones).
 *
 * @param value the mobile number to check.
 */
export const validateMobileNumber = (value?: string): ValidationResult => {
  if (!value || !value.length) {
    return ERROR_MOBILE_NUMBER_MISSING
  }

  const reg = /^(?:\+?61)?(?:\(0\)[45]|\(?0?[45]\)?)\d{8}$/

  if (!reg.test(value.replace(/\s/g, ''))) {
    return ERROR_MOBILE_NUMBER_INVALID
  }

  return undefined
}

export const validateMaximumStringLength = (
  maxLength: number,
  value?: string
): ValidationResult => {
  if (value && value.length > maxLength) {
    return createErrorMaximumStringLengthExceeded(maxLength)
  }

  return undefined
}

export const validateDeliveryMethod = (value?: string): ValidationResult => {
  if (
    Object.keys(DeliveryMethods).filter(key => DeliveryMethods[key] === value)
      .length < 1
  ) {
    return ERROR_DELIVERY_METHOD_REQUIRED
  }
  return undefined
}

export const validateDeliveryDate = (
  maxDeliveryDate: number,
  value?: string
): ValidationResult => {
  if (!value || !moment(value, moment.ISO_8601).isValid()) {
    return ERROR_DELIVERY_DATE_INVALID
  }

  const duration = moment.duration(moment(value).diff(moment()))

  if (duration.asMonths() > maxDeliveryDate) {
    return createErrorDeliveryDateValidRangeExceeded(maxDeliveryDate)
  }

  return undefined
}

export const validateAmount = (
  min: number,
  max: number,
  value?: Currency | string
): ValidationResult => {
  if (!value) {
    return ERROR_AMOUNT_REQUIRED
  }

  // check if value is Currency
  // when submit is clicked, it bypasses card-select-custom-amount conversion of string to Currency
  if (typeof value === 'string') {
    value = new Currency(value)
  }

  const result = value.toNumber()
  if (result < min || result > max) {
    return ERROR_CUSTOM_AMOUNT_INVALID.replace(
      /{minAmount}/,
      min.toString()
    ).replace(/{maxAmount}/, max.toString())
  }

  return undefined
}

export const validatePersonaliseToName = (value?: string): ValidationResult => {
  if (isEmpty(value)) {
    return ERROR_RECIPIENT_NAME_REQUIRED
  }

  if (!validateStrMaxLen(value)) {
    return ERROR_RECIPIENT_NAME
  }

  return undefined
}

export const validatePersonaliseFromName = (
  value?: string
): ValidationResult => {
  if (isEmpty(value)) {
    return ERROR_FROM_NAME_REQUIRED
  }

  if (!validateStrMaxLen(value)) {
    return ERROR_FROM_NAME
  }

  return undefined
}

export const validatePersonaliseCustomMessage = (
  value?: string
): ValidationResult => {
  if (
    !isEmpty(value) &&
    !validateStrMaxLen(value, CUSTOM_PERSONAL_MESSAGE_MAX_CHARACTERS)
  ) {
    return ERROR_CUSTOM_PERSONAL_MESSAGE
  }

  if (value === LABEL_CUSTOM_MESSAGE || isEmpty(value)) {
    return ERROR_CUSTOM_MESSAGE_REQUIRED
  }

  return undefined
}

export const validateEmptyField = (
  value: string | undefined,
  fieldName: string,
  type?: MarketplaceProductOptionType
): string | undefined => {
  if (!isEmpty(value)) return undefined

  let errorMessage
  switch (type) {
    case MarketplaceProductOptionType.DROPDOWN:
      errorMessage = `Please select a ${fieldName}`
      break

    default:
      errorMessage = `Please enter a ${fieldName}`
  }
  return errorMessage
}

export const validateTermsAndConditions = (
  value: boolean | undefined
): string => (!value ? TERMS_AND_CONDITIONS_REQUIRED : '')

export const validatePostCode = (
  countryCode: string | undefined,
  postCode: string | undefined
): string | undefined => {
  if (!postCode) {
    return 'Please enter a valid postcode'
  }
  if (!countryCode) {
    return 'Please enter a valid country'
  }
  if (postcodeValidatorExistsForCountry(countryCode)) {
    return postcodeValidator(postCode, countryCode)
      ? undefined
      : 'Please enter a valid postcode'
  }
  return validateUnknownPostcode(postCode)
}

const validateUnknownPostcode = (postcode: string): string | undefined => {
  if (/^(\w|-){3,10}$/.test(postcode)) {
    return undefined
  }
  return 'Please enter a valid postcode'
}
